import type { ComponentEventHandler } from '@archipro-design/aria';
import {
    Button,
    NumberInput,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import * as S from './QuantityGroup.style';
import { PlusLight, LightMinus } from '@archipro-design/icons';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useVariantStock } from '../../hook/use-variant-stock';
import React from 'react';
import { useAddToCart } from '@modules/ecommerce/hook/use-add-to-cart';
import SaveToDesignBoard from '@modules/design-board/component/SaveToDesignBoard';
import { useLoaderData } from '@remix-run/react';
import { useTracker } from '@archipro-website/tracker';
import { getProductGA4Data } from '~/modules/tracking/util/ga4Helper';
import { useToaster } from '~/modules/root/context/ToasterContext';
import { getEcomEventData } from '~/modules/tracking/util/gtmHelper';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { fetcherIsDone } from '~/utils/fetcherHelper';
import { useFavouriteVariant } from '../../hook/use-favourite-variant';
import type { CartItem } from '@modules/ecommerce/type/cart-item';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import { useCartPopupState } from '~/modules/ecommerce/context/CartPopupContext';

interface PurchaseButtonGroupProps {
    onItemAdded?: (item: CartItem | null) => void;
}

const PurchaseButtonGroup = ({ onItemAdded }: PurchaseButtonGroupProps) => {
    const { quantity, setQuantity, selectedVariant } =
        useVariantSelectionState();
    const { product } = useLoaderData<ProductPageLoaderData>();
    const { hasClickAndCollect, hasShippingType } = useVariantShipping();
    const { canAddToCart, quantityLimit } = useVariantStock({
        hasClickAndCollect,
        hasShippingType,
    });

    const { onChange: onCartPopupChange } = useCartPopupState();
    const [addToCart, fetcher] = useAddToCart(false);
    const favouriteItem = useFavouriteVariant(product.ID);

    const tracker = useTracker();

    const { toast } = useToaster();

    const { css } = useStyles({ canAddToCart });
    const {
        siteVariables: { colors },
    } = useTheme();

    const onInputChange: ComponentEventHandler<number | null> = (_, data) => {
        const newQuantity = data ?? -1;
        setQuantity(newQuantity);
    };

    const onInputBlur = () => {
        if (quantity < 1) {
            setQuantity(1);
        }
    };
    const onAddToCartClick = () => {
        if (!selectedVariant) return;
        addToCart(selectedVariant, quantity);
        onCartPopupChange(true);
    };

    const isLoading = fetcher.state !== 'idle';

    React.useEffect(() => {
        if (fetcherIsDone(fetcher) && fetcher.data) {
            if ('error' in fetcher.data) {
                toast(fetcher.data.message || 'Unable to add to cart', {
                    type: 'error',
                });
            } else {
                const lastAddedItem =
                    fetcher.data?.addToShoppingCart.LastAddedItems[0];
                if (lastAddedItem) {
                    const ga4Data = getProductGA4Data({
                        product,
                        selectedVariant,
                        quantity,
                    });
                    tracker.log('AddToCart', {
                        url: new URL(window.location.href),
                        data: {
                            // format: CartItemID
                            ExtraData: `${lastAddedItem.ID || 0}`,
                        },
                    });
                    // ecom event for remarketing
                    tracker.log('add_to_cart', {
                        url: new URL(window.location.href),
                        targetTracker: 'googleTagManagerTracker',
                        gtmData: getEcomEventData(ga4Data),
                    });
                    onItemAdded &&
                        onItemAdded({
                            ...lastAddedItem,
                        });
                }
            }
        }
    }, [
        fetcher,
        product,
        quantity,
        selectedVariant,
        toast,
        tracker,
        onItemAdded,
    ]);

    return (
        <div className={css(S.QuantityGroup)}>
            <div className={`${css(S.PurchaseGroup)} purchase-group`}>
                <Button
                    className={css(S.ButtonBase, S.ButtonSubstract)}
                    icon={<LightMinus />}
                    transparent
                    size={24}
                    variables={{
                        primaryButtonBackgroundColorNormal:
                            colors.white['100A'],
                        buttonPadding: 0,
                        iconPadding: 0,
                    }}
                    disabled={
                        !selectedVariant || !canAddToCart || quantity <= 1
                    }
                    onClick={() => setQuantity(quantity - 1)}
                />
                <NumberInput
                    className={css(S.BoxInput)}
                    disabled={!canAddToCart}
                    value={quantity < 0 ? null : quantity}
                    onChange={onInputChange}
                    min={0}
                    max={quantityLimit}
                    onBlur={onInputBlur}
                    maxLength={quantityLimit.toString().length}
                    allowsEmpty={true}
                />
                <Button
                    className={css(S.ButtonBase, S.ButtonAdd)}
                    icon={<PlusLight />}
                    transparent
                    size={24}
                    variables={{
                        primaryButtonBackgroundColorNormal:
                            colors.white['100A'],
                        buttonPadding: 0,
                        iconPadding: 0,
                    }}
                    disabled={
                        !selectedVariant ||
                        !canAddToCart ||
                        quantity >= quantityLimit
                    }
                    onClick={() => setQuantity(quantity + 1)}
                />
                <Button
                    className={css(S.ButtonAddToCart)}
                    disabled={!canAddToCart}
                    size={16}
                    fluid
                    onClick={onAddToCartClick}
                    loading={isLoading}
                    variables={{
                        iconPadding: 0,
                        buttonPadding: 0,
                    }}
                >
                    Add to cart
                </Button>
                <SaveToDesignBoard
                    heartButton={{
                        className: css(
                            S.ButtonBase,
                            S.ButtonHeart,
                            S.SaveToDesignBoard
                        ),
                        color: 'primary',
                        variables: {
                            iconPadding: 0,
                            buttonPadding: 0,
                        },
                        size: 20,
                    }}
                    {...favouriteItem}
                />
            </div>
        </div>
    );
};

export default PurchaseButtonGroup;
